"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Check, Layers } from "lucide-react";

type Group = {
  label: { en: string; es: string };
  chips: {
    label: { en: string; es: string };
    selected?: boolean;
    delta?: string;
  }[];
};

const GROUPS: Group[] = [
  {
    label: { en: "Tortilla", es: "Tortilla" },
    chips: [
      { label: { en: "Corn", es: "Maíz" }, selected: true },
      { label: { en: "Flour", es: "Harina" } },
      { label: { en: "Crispy", es: "Crujiente" }, delta: "+$0.50" },
    ],
  },
  {
    label: { en: "Protein", es: "Proteína" },
    chips: [
      { label: { en: "Birria", es: "Birria" }, selected: true },
      { label: { en: "Double meat", es: "Doble carne" }, selected: true, delta: "+$3.00" },
      { label: { en: "Half-and-half", es: "Mitad y mitad" } },
    ],
  },
  {
    label: { en: "Salsa", es: "Salsa" },
    chips: [
      { label: { en: "Roja", es: "Roja" } },
      { label: { en: "Verde", es: "Verde" }, selected: true },
      { label: { en: "Habanero", es: "Habanero" } },
      { label: { en: "Pico", es: "Pico" } },
    ],
  },
  {
    label: { en: "Extras", es: "Extras" },
    chips: [
      { label: { en: "Cheese", es: "Queso" }, selected: true, delta: "+$1.00" },
      { label: { en: "Guac", es: "Guac" }, delta: "+$2.00" },
      { label: { en: "Crema", es: "Crema" }, selected: true },
      { label: { en: "Cilantro", es: "Cilantro" } },
      { label: { en: "Onion", es: "Cebolla" } },
    ],
  },
];

export default function RushHourModifiersScroll() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-white overflow-hidden">
      <div className="max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#D7263D" }}>
            {t("Modifiers", "Modificadores")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02] text-redy-charcoal">
            {t("Every modifier. ", "Cada modificador. ")}
            <span className="text-redy-charcoal/30">{t("One sheet.", "Una hoja.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-redy-secondary leading-relaxed mt-6">
            {t(
              "No submenus. No back buttons. No \"and what about the side?\" pop-ups. Tap an item — every option opens at once. Pick what you need, hit Add. The cashier never breaks rhythm.",
              "Sin submenús. Sin botones de regreso. Sin pop-ups de \"¿y el acompañamiento?\". Tap en un item — todas las opciones se abren a la vez. Eliges lo que necesitas, presionas Agregar. El cajero nunca pierde ritmo."
            )}
          </p>
        </motion.div>

        {/* iPad mockup with bottom-sheet open */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="relative max-w-[900px] mx-auto"
        >
          <div
            className="absolute -inset-6 rounded-[40px] pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(244,114,43,0.10) 0%, transparent 65%)" }}
          />

          <div className="relative bg-[#1a1a1a] rounded-[28px] p-3 md:p-4 shadow-[0_40px_80px_rgba(0,0,0,0.18)]">
            <div className="bg-white rounded-[20px] overflow-hidden relative">
              {/* Dim background — menu grid behind */}
              <div className="absolute inset-0 opacity-30 pointer-events-none">
                <div className="grid grid-cols-3 gap-2 p-5">
                  {Array.from({ length: 9 }).map((_, i) => (
                    <div key={i} className="aspect-square bg-redy-charcoal/10 rounded-xl" />
                  ))}
                </div>
              </div>

              {/* Bottom sheet */}
              <motion.div
                initial={{ y: 60, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.21, 0.47, 0.32, 0.98] }}
                className="relative bg-white rounded-t-[24px] mt-12 md:mt-20 shadow-[0_-20px_50px_rgba(0,0,0,0.12)] border-t border-black/5"
              >
                {/* Handle */}
                <div className="flex justify-center pt-3">
                  <div className="w-10 h-1.5 rounded-full bg-redy-charcoal/15" />
                </div>

                {/* Sheet header */}
                <div className="flex items-baseline justify-between px-6 pt-3 pb-2">
                  <div>
                    <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60">
                      {t("Customize", "Personalizar")}
                    </div>
                    <div className="text-[22px] md:text-[28px] font-black text-redy-charcoal mt-0.5">
                      {t("Birria taco", "Taco de Birria")}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-redy-secondary/60">
                      {t("Item total", "Total del item")}
                    </div>
                    <div className="text-[20px] font-black tabular-nums text-redy-charcoal mt-0.5">
                      $13.50
                    </div>
                  </div>
                </div>

                {/* Groups */}
                <div className="px-6 pb-4 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                  {GROUPS.map((g, gi) => (
                    <motion.div
                      key={g.label.en}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.45, delay: 0.4 + 0.1 * gi }}
                    >
                      <div className="text-[10px] font-black tracking-widest uppercase text-redy-secondary/60 mb-2">
                        {t(g.label.en, g.label.es)}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {g.chips.map((c, ci) => (
                          <motion.div
                            key={c.label.en}
                            initial={c.selected ? { scale: 0.85, opacity: 0 } : false}
                            whileInView={c.selected ? { scale: 1, opacity: 1 } : {}}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.35, delay: 0.5 + 0.05 * gi + 0.04 * ci }}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-bold border transition-all"
                            style={
                              c.selected
                                ? {
                                    background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                                    borderColor: "transparent",
                                    color: "white",
                                    boxShadow: "0 4px 12px rgba(244,114,43,0.25)",
                                  }
                                : {
                                    background: "white",
                                    borderColor: "rgba(0,0,0,0.10)",
                                    color: "#6B6762",
                                  }
                            }
                          >
                            {c.selected && (
                              <Check className="w-3 h-3" strokeWidth={3.5} />
                            )}
                            {t(c.label.en, c.label.es)}
                            {c.delta && (
                              <span
                                className="text-[10px] font-bold tabular-nums"
                                style={{ color: c.selected ? "rgba(255,255,255,0.8)" : "#9B9690" }}
                              >
                                {c.delta}
                              </span>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Sheet footer: Add button */}
                <div className="px-6 pb-6 pt-2">
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ duration: 0.4, delay: 1.1 }}
                    className="flex items-center justify-between gap-4"
                  >
                    <div className="text-[12px] font-bold text-redy-secondary">
                      <span className="font-black text-redy-charcoal">5</span>{" "}
                      {t("modifiers · one sheet · no submenus", "modificadores · una hoja · sin submenús")}
                    </div>
                    <div
                      className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white text-[13px] font-black tracking-wide uppercase shadow-lg"
                      style={{
                        background: "linear-gradient(135deg, #F4722B 0%, #D7263D 100%)",
                        boxShadow: "0 12px 24px rgba(244,114,43,0.30)",
                      }}
                    >
                      <Check className="w-4 h-4" strokeWidth={3.5} />
                      {t("Add · $13.50", "Agregar · $13.50")}
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>

        {/* Below caption */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-12"
        >
          <div className="inline-flex items-center gap-2 bg-[#FAFAF8] px-5 py-3 rounded-full border border-black/5">
            <Layers className="w-4 h-4" style={{ color: "#D7263D" }} />
            <span className="text-[13px] font-bold text-redy-charcoal">
              {t(
                "Five modifiers. One sheet. Seconds to Add.",
                "Cinco modificadores. Una hoja. Segundos para Agregar."
              )}
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
